'use client';

import React from 'react';
import Link from 'next/link';
import { Shield, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back to Ertzyx
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-blue-700" />
            <span className="font-bold text-slate-800">Ertzyx</span>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-3">Privacy Policy</h1>
          <p className="text-slate-500 text-sm">
            Last updated: 25 March 2026 · Effective: 25 March 2026
          </p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            This Privacy Policy explains how Ertzyx (&quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot;), operated at{' '}
            <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
              ledger.ertzyx.com
            </a>
            , collects, uses, shares, and protects your personal information. We are committed to
            compliance with the General Data Protection Regulation (GDPR), the California Consumer
            Privacy Act (CCPA), and Canada&apos;s Personal Information Protection and Electronic
            Documents Act (PIPEDA).
          </p>
        </div>

        <div className="space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Who We Are</h2>
            <p>
              Ertzyx is a SaaS platform for storing and verifying personal achievements,
              credentials, and cultural heritage records. We serve individuals and institutions
              worldwide.
            </p>
            <ul className="mt-3 space-y-1 list-disc list-inside text-slate-600">
              <li>
                Platform:{' '}
                <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
                  ledger.ertzyx.com
                </a>
              </li>
              <li>
                Contact:{' '}
                <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                  support@ertzyx.com
                </a>
              </li>
              <li>Legal entity: Ertzyx Ltd</li>
              <li>Address: [Registered address — to be updated]</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Data We Collect</h2>
            <p className="mb-3">We collect the following categories of personal data:</p>
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">Account Information</h3>
                <p className="text-sm text-slate-600">
                  Full name, email address, account type (individual or institution), and password
                  (stored as a secure hash — never in plain text).
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">Achievement Records</h3>
                <p className="text-sm text-slate-600">
                  Achievement titles, descriptions, dates, associated institutions, pillar
                  categories, verification status, and verification history including timestamps and
                  reviewer identifiers.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">Uploaded Media Files</h3>
                <p className="text-sm text-slate-600">
                  Documents, images, certificates, and other files you upload as supporting evidence
                  for your achievements.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">Usage Data</h3>
                <p className="text-sm text-slate-600">
                  IP address, browser type, pages visited, actions taken within the platform, and
                  session duration — collected for security and service improvement purposes.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">Payment Information</h3>
                <p className="text-sm text-slate-600">
                  If paid services are offered, billing details and payment information may be
                  processed by a third-party payment processing provider. Ertzyx does not
                  intentionally store full payment card numbers on its own systems.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-2">Communications</h3>
                <p className="text-sm text-slate-600">
                  Messages you send via our contact form, including name, email, organisation,
                  subject, and message content.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Data</h2>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>To create and manage your account</li>
              <li>To store, display, and share your achievement records as you direct</li>
              <li>To facilitate verification requests between individuals and institutions</li>
              <li>To process payments and manage billing through a third-party payment processing provider</li>
              <li>
                To send transactional emails (account confirmation, verification updates, password
                reset)
              </li>
              <li>To respond to support and contact form submissions</li>
              <li>To improve platform security and detect fraudulent activity</li>
              <li>To comply with legal obligations</li>
              <li>To analyse aggregate usage patterns (anonymised) for product improvement</li>
            </ul>
            <p className="mt-4 text-sm text-slate-500">
              We do not use your personal data for advertising or sell it to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              4. Legal Basis for Processing (GDPR)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Purpose</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">
                      Legal Basis
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ['Account creation and management', 'Contract performance'],
                    ['Processing payments', 'Contract performance'],
                    ['Sending transactional emails', 'Contract performance'],
                    ['Security and fraud prevention', 'Legitimate interests'],
                    ['Analytics (anonymised)', 'Legitimate interests'],
                    ['Marketing communications', 'Consent (opt-in only)'],
                    ['Legal compliance', 'Legal obligation'],
                  ]?.map(([purpose, basis]) => (
                    <tr key={purpose} className="bg-white">
                      <td className="px-4 py-3 text-slate-700">{purpose}</td>
                      <td className="px-4 py-3 text-slate-500">{basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              5. Who We Share Your Data With
            </h2>
            <p className="mb-4">
              We do not sell, rent, or trade your personal information.
            </p>
            <p className="mb-4 text-slate-600">
              We may share limited personal information with trusted third-party service providers
              only when necessary to operate Ertzyx, process payments, provide verification
              features, maintain platform security, comply with legal obligations, or deliver
              services requested by users.
            </p>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Platform Operations Providers</h3>
                <p className="text-sm text-slate-600">
                  We use trusted service providers to support hosting, authentication, database
                  storage, file storage, system security, and platform operations. These providers
                  may process limited personal information only as needed to provide services to
                  Ertzyx.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Payment Processing Providers</h3>
                <p className="text-sm text-slate-600">
                  If paid services are offered, payment information may be processed by a
                  third-party payment processing provider. Ertzyx does not intentionally store full
                  payment card details on its own systems.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Partner Institutions</h3>
                <p className="text-sm text-slate-600">
                  When a user submits an achievement, credential, or record for verification, the
                  relevant institution may receive the information needed to review or verify that
                  submission. Partner institutions do not receive unrelated profile data unless the
                  user chooses to share it.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Public Credential Pages</h3>
                <p className="text-sm text-slate-600">
                  If a user chooses to make a credential, record, or profile public, it may be
                  accessible to anyone with the relevant link or through the public page. Users
                  control what they make public.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Legal Authorities</h3>
                <p className="text-sm text-slate-600">
                  We may disclose personal information if required by law, court order, regulatory
                  request, or when necessary to protect the rights, safety, and security of Ertzyx,
                  our users, or the public.
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-500 font-medium">
              We require service providers to handle personal information only for authorised
              purposes and in accordance with applicable privacy and security obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Cookies</h2>
            <p className="mb-3">We use the following types of cookies:</p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>
                <strong>Essential cookies:</strong> Required for authentication and session
                management. Cannot be disabled.
              </li>
              <li>
                <strong>Preference cookies:</strong> Remember your cookie consent choice.
              </li>
              <li>
                <strong>Analytics cookies:</strong> Used to understand how visitors use the platform
                (only with your consent).
              </li>
            </ul>
            <p className="mt-3">
              You can manage cookie preferences via our cookie consent banner or your browser
              settings. See our{' '}
              <Link href="/cookie-policy" className="text-blue-700 underline">
                Cookie Policy
              </Link>{' '}
              for full details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide
              services. See our{' '}
              <Link href="/data-retention" className="text-blue-700 underline">
                Data Retention Policy
              </Link>{' '}
              for full details on retention periods and deletion schedules.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Your Rights</h2>
            <p className="mb-3">Depending on your jurisdiction, you have the following rights:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ['Right of Access', 'Request a copy of all personal data we hold about you.'],
                ['Right to Rectification', 'Correct inaccurate or incomplete data.'],
                ['Right to Erasure', 'Request deletion of your account and all associated data.'],
                ['Right to Portability', 'Receive your data in a machine-readable format.'],
                [
                  'Right to Restrict Processing',
                  'Limit how we use your data in certain circumstances.',
                ],
                ['Right to Object', 'Object to processing based on legitimate interests.'],
                [
                  'Right to Withdraw Consent',
                  'Withdraw consent for marketing or analytics at any time.',
                ],
                [
                  'CCPA Rights (California)',
                  'Know, delete, opt-out of sale, and non-discrimination rights.',
                ],
              ]?.map(([right, desc]) => (
                <div key={right} className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="font-semibold text-slate-800 text-sm mb-1">{right}</p>
                  <p className="text-xs text-slate-500">{desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-4">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                support@ertzyx.com
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              9. How to Delete Your Account and Data
            </h2>
            <p className="mb-3">
              To request full deletion of your account and all associated data:
            </p>
            <ol className="space-y-2 list-decimal list-inside text-slate-600">
              <li>Log in to your Ertzyx account</li>
              <li>Navigate to Settings → Account → Delete Account</li>
              <li>Confirm deletion — this action is irreversible</li>
            </ol>
            <p className="mt-3">
              Alternatively, email{' '}
              <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                support@ertzyx.com
              </a>{' '}
              with the subject &quot;Account Deletion Request&quot;. We will process your request
              within 30 days. Note that some data may be retained for legal compliance purposes
              (e.g., payment records) as outlined in our{' '}
              <Link href="/data-retention" className="text-blue-700 underline">
                Data Retention Policy
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              10. International Data Transfers
            </h2>
            <p>
              Your data may be processed in countries outside your own, including the United States
              and the European Union, where our trusted service providers operate. We ensure
              appropriate safeguards are in place, including Standard Contractual Clauses (SCCs)
              where required by GDPR.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Security</h2>
            <p>
              We implement industry-standard security measures including TLS encryption in transit,
              AES-256 encryption at rest, row-level security on all database tables, and regular
              security reviews. However, no system is completely secure. Please use a strong, unique
              password and enable two-factor authentication where available.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Children&apos;s Privacy</h2>
            <p>
              Ertzyx is not directed at children under 16. We do not knowingly collect personal data
              from children. If you believe a child has provided us with personal data, contact us
              immediately at{' '}
              <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                support@ertzyx.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material
              changes via email or a prominent notice on the platform. Continued use of Ertzyx after
              changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact Us</h2>
            <p>For privacy-related questions, requests, or complaints:</p>
            <div className="mt-3 bg-white border border-slate-200 rounded-xl p-5">
              <p className="font-semibold text-slate-800">Ertzyx Privacy Team</p>
              <p className="text-slate-600 text-sm mt-1">
                Email:{' '}
                <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                  support@ertzyx.com
                </a>
              </p>
              <p className="text-slate-600 text-sm">
                Platform:{' '}
                <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
                  ledger.ertzyx.com
                </a>
              </p>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              If you are in the EU and are not satisfied with our response, you have the right to
              lodge a complaint with your local data protection authority.
            </p>
          </section>
        </div>
      </div>
      {/* Footer nav */}
      <div className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap gap-4 text-sm text-slate-500">
          <Link href="/terms-of-service" className="hover:text-slate-800 transition-colors">
            Terms of Service
          </Link>
          <Link href="/data-retention" className="hover:text-slate-800 transition-colors">
            Data Retention
          </Link>
          <Link href="/cookie-policy" className="hover:text-slate-800 transition-colors">
            Cookie Policy
          </Link>
          <Link href="/contact" className="hover:text-slate-800 transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
